
import React, { useState, useEffect } from 'react';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { PipelineColumn } from './PipelineColumn';
import { LoadingState } from '../error-handling/LoadingState';
import { Card, CardContent } from "@/components/ui/card";
import { useCandidates } from '@/hooks/useCandidates';
import { toast } from 'sonner';
import { HiredCandidateForm } from './forms/HiredCandidateForm';
import { RejectedCandidateForm } from './forms/RejectedCandidateForm';
import type { Candidate } from './types';
import { getA2AAgentForStatus } from '@/utils/a2a/orchestration/adaptive';

const statusColumns = [
  { id: 'new', label: 'New Candidates' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'interview', label: 'Interview' },
  { id: 'offer', label: 'Offer' },
  { id: 'hired', label: 'Hired' },
  { id: 'rejected', label: 'Rejected' }
];

const CandidatePipeline = () => {
  const { data: candidates = [], isLoading, updateCandidateStatus } = useCandidates();
  const [showHiredForm, setShowHiredForm] = useState(false);
  const [showRejectedForm, setShowRejectedForm] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);

  const organizedCandidates = statusColumns.reduce((acc, column) => {
    acc[column.id] = candidates.filter(candidate => candidate.status === column.id);
    return acc;
  }, {} as Record<string, Candidate[]>);

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    const candidate = candidates.find(c => c.id === draggableId);
    if (!candidate) return;

    const newStatus = destination.droppableId;
    const oldStatus = candidate.status;

    // Check if moving to hired status and candidate needs hiring details
    if (newStatus === 'hired' && !candidate.salary_amount && !candidate.start_date) {
      setSelectedCandidate(candidate);
      setShowHiredForm(true);
      return;
    }

    // Check if moving to rejected status
    if (newStatus === 'rejected') {
      setSelectedCandidate(candidate);
      setShowRejectedForm(true);
      return;
    }

    // For other status changes, update immediately
    updateCandidateStatus(
      { candidateId: candidate.id, status: newStatus, oldStatus },
      {
        onSuccess: () => {
          toast.success(`${candidate.first_name || 'Candidate'} moved to ${newStatus}`);
        },
        onError: (error) => {
          console.error('Error updating candidate status:', error);
          toast.error('Failed to update candidate status');
        },
      }
    );
  };

  const handleMarkAsHired = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setShowHiredForm(true);
  };

  const handleHiredFormSave = (updatedCandidate: Candidate) => {
    // The form already handles the database update, just close the form
    setShowHiredForm(false);
    setSelectedCandidate(null);
  };

  const handleRejectedFormSave = (updatedCandidate: Candidate) => {
    // The form already handles the database update, just close the form
    setShowRejectedForm(false);
    setSelectedCandidate(null);
  };

  const handleFormClose = () => {
    setShowHiredForm(false);
    setShowRejectedForm(false);
    setSelectedCandidate(null);
  };

  if (isLoading) {
    return <LoadingState message="Loading candidates..." />;
  }

  return (
    <div className="w-full">
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4 min-h-[600px]">
          {statusColumns.map((column) => {
            const agent = getA2AAgentForStatus(column.id);
            return (
              <PipelineColumn
                key={column.id}
                status={column.id}
                label={column.label}
                candidates={organizedCandidates[column.id] || []}
                agent={agent}
                onMarkAsHired={handleMarkAsHired}
              />
            );
          })}
        </div>
      </DragDropContext>

      {/* Show empty state if no candidates */}
      {candidates.length === 0 && (
        <Card className="mt-8">
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">
              No candidates in your pipeline yet. Start by adding some candidates!
            </p>
          </CardContent>
        </Card>
      )}

      {/* Hired Candidate Form */}
      {showHiredForm && selectedCandidate && (
        <HiredCandidateForm
          candidate={selectedCandidate}
          isOpen={showHiredForm}
          onClose={handleFormClose}
          onSave={handleHiredFormSave}
        />
      )}

      {/* Rejected Candidate Form */}
      {showRejectedForm && selectedCandidate && (
        <RejectedCandidateForm
          candidate={selectedCandidate}
          open={showRejectedForm}
          onClose={handleFormClose}
          onSave={handleRejectedFormSave}
        />
      )}
    </div>
  );
};

export default CandidatePipeline;
